import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type PushPermission = NotificationPermission | "unsupported";

interface PushState {
  isSupported: boolean;
  permission: PushPermission;
  isEnabled: boolean;
  isLoading: boolean;
  deniedReason: "unsupported" | "denied" | "no-config" | null;
}

const vapidPublicKey = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY as string | undefined;

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const pushSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  window.isSecureContext;

const readSubscription = async () => {
  try {
    const registration =
      (await navigator.serviceWorker.getRegistration()) ??
      (await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<ServiceWorkerRegistration>((_, reject) => 
          setTimeout(() => reject(new Error("SW ready timeout")), 1500)
        )
      ]).catch(() => null));
    
    if (!registration) return { registration: null, subscription: null };
    const subscription = await registration.pushManager.getSubscription();
    return { registration, subscription };
  } catch (err) {
    console.debug("[PushNotifications] Error reading subscription:", err);
    return { registration: null, subscription: null };
  }
};

export function useWebPushNotifications() {
  const { user } = useAuth();
  const [state, setState] = useState<PushState>({
    isSupported: pushSupported(),
    permission: pushSupported() ? Notification.permission : "unsupported",
    isEnabled: false,
    isLoading: false,
    deniedReason: pushSupported() ? (Notification.permission === "denied" ? "denied" : null) : "unsupported",
  });

  const syncSubscription = useCallback(async () => {
    if (!user || !pushSupported()) {
      setState((current) => ({
        ...current,
        isSupported: pushSupported(),
        permission: pushSupported() ? Notification.permission : "unsupported",
        isEnabled: false,
        isLoading: false,
        deniedReason: !pushSupported() ? "unsupported" : (Notification.permission === "denied" ? "denied" : null),
      }));
      return;
    }

    if (!vapidPublicKey) {
      setState((current) => ({ ...current, isLoading: false, deniedReason: "no-config" }));
      return;
    }

    try {
      setState((current) => ({ ...current, isLoading: true }));
      const { subscription } = await readSubscription();
      const isEnabled = Boolean(subscription && Notification.permission === "granted");

      if (subscription) {
        const rawP256dh = subscription.getKey("p256dh");
        const rawAuth = subscription.getKey("auth");

        if (rawP256dh && rawAuth) {
          const p256dh = window.btoa(String.fromCharCode(...new Uint8Array(rawP256dh)));
          const auth = window.btoa(String.fromCharCode(...new Uint8Array(rawAuth)));

          await supabase
            .from("push_subscriptions" as never)
            .upsert(
              {
                user_id: user.id,
                endpoint: subscription.endpoint,
                p256dh,
                auth,
                user_agent: navigator.userAgent,
                is_active: true,
                last_seen_at: new Date().toISOString(),
              } as never,
              { onConflict: "user_id,endpoint" },
            );
        }
      }

      setState((current) => ({
        ...current,
        isEnabled,
        permission: Notification.permission,
        isLoading: false,
        deniedReason: Notification.permission === "denied" ? "denied" : null,
      }));
    } catch {
      setState((current) => ({ ...current, isLoading: false }));
    }
  }, [user]);

  const enable = useCallback(async () => {
    if (!user || !pushSupported() || !vapidPublicKey) return false;

    try {
      setState((current) => ({ ...current, isLoading: true }));

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState((current) => ({ ...current, permission, isEnabled: false, isLoading: false, deniedReason: permission === "denied" ? "denied" : null }));
        return false;
      }

      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      const { registration } = await readSubscription();
      if (!registration) {
        setState((current) => ({ ...current, permission, isLoading: false }));
        return false;
      }
      const existingSubscription = await registration.pushManager.getSubscription();
      const subscription =
        existingSubscription ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        }));

      const rawP256dh = subscription.getKey("p256dh");
      const rawAuth = subscription.getKey("auth");

      if (!rawP256dh || !rawAuth) {
        setState((current) => ({ ...current, isLoading: false }));
        return false;
      }

      const p256dh = window.btoa(String.fromCharCode(...new Uint8Array(rawP256dh)));
      const auth = window.btoa(String.fromCharCode(...new Uint8Array(rawAuth)));

      const { error } = await supabase
        .from("push_subscriptions" as never)
        .upsert(
          {
            user_id: user.id,
            endpoint: subscription.endpoint,
            p256dh,
            auth,
            user_agent: navigator.userAgent,
            is_active: true,
            last_seen_at: new Date().toISOString(),
          } as never,
          { onConflict: "user_id,endpoint" },
        );

      if (error) {
        setState((current) => ({ ...current, isLoading: false }));
        return false;
      }

      setState((current) => ({
        ...current,
        permission,
        isEnabled: true,
        isLoading: false,
        deniedReason: null,
      }));

      return true;
    } catch {
      setState((current) => ({ ...current, isLoading: false }));
      return false;
    }
  }, [user]);

  const disable = useCallback(async () => {
    if (!user || !pushSupported()) return false;

    try {
      setState((current) => ({ ...current, isLoading: true }));
      const { subscription } = await readSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await supabase
          .from("push_subscriptions" as never)
          .update({ is_active: false } as never)
          .eq("user_id", user.id)
          .eq("endpoint", subscription.endpoint);
      }

      setState((current) => ({ ...current, isEnabled: false, isLoading: false }));
      return true;
    } catch {
      setState((current) => ({ ...current, isLoading: false }));
      return false;
    }
  }, [user]);

  useEffect(() => {
    void syncSubscription();
  }, [syncSubscription]);

  return useMemo(
    () => ({
      ...state,
      hasConfig: Boolean(vapidPublicKey),
      enable,
      disable,
      refresh: syncSubscription,
    }),
    [disable, enable, state, syncSubscription],
  );
}
